import { parseISO } from "date-fns";
import { GraphQLFormattedError } from "graphql";
import { Card, Flex, FullscreenTakeover, Text, toast } from "@jsq/ds/core";
import { CancelButton, Form, SubmitButton } from "@jsq/ds/forms";
import { Column } from "@jsq/ds/layouts";
import { AddressType } from "~/graphql/mainEnums";
import { IndividualAccountTypeEnum, ProfileRelationshipEnum } from "~/graphql/supergraphEnums";
import {
  useCreateIndividualProfileMutation,
  useOverrideRiskRatingMutation,
  useUpdateIndividualProfileMutation,
  useUpdatePicrPositionDetailsMutation,
} from "~/graphql/supergraphHooks";
import {
  type ComplianceIndividualProfileInput,
  type RiskRating,
} from "~/graphql/supergraphTypes";
import RiskRatingRadioBar from "~/pages/Compliance/CaseDetailsDrawer/Profiles/common/RiskRatingRadioBar";
import { ProfileDetailsType } from "~/pages/Compliance/CaseDetailsDrawer/Profiles/types";
import {
  getOwnershipPercentage,
  getTitle,
} from "~/pages/Compliance/CaseDetailsDrawer/Profiles/utils";
import { useCurrentCaseContext } from "~/pages/Compliance/context/CurrentCaseContext";
import { useGuidedProfileCreation } from "~/pages/Compliance/flags";
import { shouldShowRiskRating } from "~/pages/Compliance/utils";
import {
  isIndividualAccountType,
  isJointAccountType,
} from "~/pages/Compliance/utils/accountTypeUtils";
import { getProfilesMapById } from "~/pages/Compliance/utils/profileMaputils";
import { toISOFormat } from "~/utils/datetime";
import AddressFields from "../../AddressFields";
import IndividualFormFields from "../../IndividualFields";
import { buildRelationshipTypes } from "../../ProfileActions/utils";
import ProfileIndividualFormFields from "../../ProfileIndividualFormFields";
import { getSchema } from "./schema";
import * as styles from "./styles.css";
import { FormValues } from "./types";

interface Props {
  caseId: number;
  complianceProfile: ProfileDetailsType | undefined;
  programId: number;
  positionId?: string | null;
  isBeneficiaryProfile?: boolean;
  saveButtonLabel?: string;
  onCancel?: () => void;
  onCompleted: () => void;
}

const EditIndividualForm = ({
  caseId,
  complianceProfile,
  programId,
  positionId,
  isBeneficiaryProfile = false,
  saveButtonLabel = "Save and exit",
  onCancel,
  onCompleted,
}: Props) => {
  const { aiExtractionSupported, arenaAccessId } = useCurrentCaseContext();
  const guidedProfileCreation = useGuidedProfileCreation();
  const showRiskRating = shouldShowRiskRating(programId);
  const childProfile = complianceProfile;
  const dateOfBirth = complianceProfile?.dateOfBirth
    ? parseISO(complianceProfile.dateOfBirth)
    : undefined;
  const address = complianceProfile?.addresses.find((x) => x.isPrimaryDomicile);
  const stateOrProvince = address?.state ? address?.state?.code : address?.province;

  const isOwner = complianceProfile?.parentRelationships
    ?.map((pr) => pr.relationshipType)
    .includes(ProfileRelationshipEnum.Owner)
    ? true
    : false;
  const isController = complianceProfile?.parentRelationships
    ?.map((pr) => pr.relationshipType)
    .includes(ProfileRelationshipEnum.Controller)
    ? true
    : false;

  const profileMap = getProfilesMapById(complianceProfile?.complianceCases[0]?.complianceCase);
  const investingProfileObj = complianceProfile?.complianceCases.find(
    (ccp) => ccp.complianceCase?.id === caseId,
  );
  const isInvestingProfile = investingProfileObj?.isInvestingProfile;
  const parentProfileId = complianceProfile?.parentRelationships[0]?.parentProfileId;
  let isChildOfJointAccount = false;
  if (parentProfileId) {
    const parentProfile = profileMap.get(parentProfileId);
    isChildOfJointAccount = isJointAccountType(parentProfile?.accountType);
  }

  const childAddress = childProfile?.addresses.find((x) => x.isPrimaryDomicile);
  const firstName = childProfile?.firstName
    ? (childProfile?.firstName ?? "")
    : (childProfile?.legalName ?? "");

  const resolvedAccountType = isIndividualAccountType(childProfile?.accountType)
    ? (childProfile?.accountType as IndividualAccountTypeEnum)
    : IndividualAccountTypeEnum.Individual;

  const initialValues: FormValues = {
    accountType: guidedProfileCreation
      ? resolvedAccountType
      : IndividualAccountTypeEnum.Individual,
    firstName,
    middleName: childProfile?.middleName ?? "",
    lastName: childProfile?.lastName ?? "",
    dateOfBirth: dateOfBirth,
    streetLine1: childAddress?.street ?? "",
    streetLine2: childAddress?.street2 ?? "",
    country: childAddress?.country?.alpha2code ?? "",
    city: childAddress?.city ?? "",
    stateOrProvince: stateOrProvince ?? "",
    zipCode: childAddress?.postalCode ?? "",
    taxId: childProfile?.taxIdUnencrypted ?? "",
    taxIdType: childProfile?.taxIdType,
    ownershipPercentage: isChildOfJointAccount
      ? 0
      : (getOwnershipPercentage(complianceProfile?.parentRelationships) ?? 0),
    isOwner: guidedProfileCreation && isChildOfJointAccount ? true : isOwner,
    isController: guidedProfileCreation && isChildOfJointAccount ? false : isController,
    title: getTitle(complianceProfile?.parentRelationships) ?? "",
    riskRating: complianceProfile?.riskRating?.overriddenRating ?? "",
  };

  const [createIndividualProfile] = useCreateIndividualProfileMutation({
    fetchPolicy: "network-only",
    onError: (e) => toast.error(e.message),
    refetchQueries: ["GetComplianceCase", "GetCaseInvestingProfileRiskRating", "GetPositions"],
    awaitRefetchQueries: true,
  });

  const [updateIndividualProfile] = useUpdateIndividualProfileMutation({
    fetchPolicy: "network-only",
    onError: (e) => toast.error(e.message),
    refetchQueries: ["GetComplianceCase", "GetCaseInvestingProfileRiskRating", "GetPositions"],
    awaitRefetchQueries: true,
  });

  const [overrideRiskRating] = useOverrideRiskRatingMutation({
    fetchPolicy: "network-only",
    onError: (e) => toast.error(e.message),
    refetchQueries: ["GetComplianceCase", "GetCaseInvestingProfileRiskRating", "GetPositions"],
    awaitRefetchQueries: true,
  });

  const [updatePicrPositionDetails] = useUpdatePicrPositionDetailsMutation({
    refetchQueries: ["GetPicrPositionDetails"],
    awaitRefetchQueries: true,
    fetchPolicy: "network-only",
    onError: (e) => {
      toast.error(e.message);
      throw e;
    },
  });

  /**
   * Overrides the risk rating if the override has been set. This calls a mutation.
   *
   * @param riskRating The risk rating value to override (if blank, function is a noop)
   * @param complianceProfile The profile to override
   */
  async function callOverrideRiskRating(riskRating: string) {
    if (showRiskRating && riskRating && complianceProfile?.id) {
      await overrideRiskRating({
        variables: {
          data: {
            profileId: complianceProfile.id,
            programId: programId,
            overriddenRating: riskRating as RiskRating,
          },
        },
      });
    }
  }

  /**
   * Inserts or updates (depending on the operation) an individual profile. The upsert command
   * here could handle both, but has some wacky behavior for updates so has been replaced with
   * a specific update command leaving the upsert command as just an insert. Whether we insert
   * or update is based on the whether or not complianceProfile has an ID. The value returned
   * is the ID of the new profile.
   *
   * Given this is an edit form, presumably we'll never do an insert, but because the
   * complianceProfile can be undefined, we need to allow for the insertion route.
   *
   * @param data The individual data meant for the upsert command (while be adapted for the
   *             update)
   * @returns The new profile ID
   */
  async function insertOrUpdateIndividualProfile(
    data: ComplianceIndividualProfileInput,
  ): Promise<[number | undefined, ReadonlyArray<GraphQLFormattedError> | undefined]> {
    const updatedData = {
      ...data,
      dateOfBirth: data.dateOfBirth ? toISOFormat(data.dateOfBirth) : undefined,
    };

    if (complianceProfile?.id) {
      const response = await updateIndividualProfile({
        variables: {
          data: updatedData,
          profileId: complianceProfile.id,
          parentProfileId: complianceProfile.parentRelationships[0]?.parentProfileId,
        },
      });

      return [response?.data?.updateIndividualProfile?.id, response?.errors];
    } else {
      const response = await createIndividualProfile({
        variables: {
          arenaAccessId: arenaAccessId ?? 0,
          data: updatedData,
          parentProfileId: complianceProfile?.parentRelationships[0]?.parentProfileId,
        },
      });

      return [response?.data?.createIndividualProfile?.id, response?.errors];
    }
  }

  async function editIndividual(data: ComplianceIndividualProfileInput, riskRating: string) {
    try {
      await callOverrideRiskRating(riskRating);

      const [childProfileId, errors] = await insertOrUpdateIndividualProfile(data);

      if (errors || !childProfileId) {
        toast.error("Failed to edit individual profile");
        return;
      }
      if (positionId) {
        await updatePicrPositionDetails({
          variables: {
            data: {
              caseId: caseId,
              positionId: positionId,
              createdProfileId: childProfileId,
            },
          },
        });
      }

      toast.success("Profile updated successfully");
      onCompleted();
    } catch {
      // errors already toasted via onError
    }
  }

  const handleSubmit = async (values: FormValues) => {
    const profileValues = {
      taxId: values.taxId,
      taxIdType: values.taxIdType,
      caseId: caseId,
      addresses: [
        {
          addressType: AddressType.Unknown, // We should ask for this?
          street: values.streetLine1,
          street2: values.streetLine2,
          city: values.city,
          state: values.stateOrProvince,
          postalCode: values.zipCode,
          country: values.country,
        },
      ],
    };

    const roles = isBeneficiaryProfile ? undefined : buildRelationshipTypes(values);

    const individualValues = {
      accountType: values.accountType,
      firstName: values.firstName,
      middleName: values.middleName,
      lastName: values.lastName,
      dateOfBirth: values.dateOfBirth?.toISOString() ?? "",
      ownershipPercentage: values.ownershipPercentage,
      relationshipType: roles,
      title: values.title,
    };

    const data = {
      ...profileValues,
      ...individualValues,
    };

    await editIndividual(data, values.riskRating);
  };

  return (
    <FullscreenTakeover.Body>
      <Form
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={getSchema(
          !(isInvestingProfile || positionId || isBeneficiaryProfile),
          isChildOfJointAccount,
        )}
        submitOnlyChangedValues={false}
      >
        <Column size="lg">
          <Card className={styles.editIndividualCard}>
            <Text marginBottom="md" variant="h3">
              Edit Individual
            </Text>
            {!(isInvestingProfile || positionId || isBeneficiaryProfile) && (
              <ProfileIndividualFormFields isChildOfJointAccount={isChildOfJointAccount} />
            )}
            <IndividualFormFields
              includeTitle={!isInvestingProfile}
              isEditMode
              aiExtractionSupported={aiExtractionSupported}
              isChildOfJointAccount={isChildOfJointAccount}
            />
            <AddressFields editMode aiExtractionSupported={aiExtractionSupported} />
            {showRiskRating && (
              <RiskRatingRadioBar
                form="individual"
                nonOverrideRiskRating={childProfile?.riskRating?.calculatedRating}
              />
            )}
          </Card>
          <Flex justifyContent="flex-end">
            {onCancel && (
              <CancelButton onClick={onCancel} className={styles.editIndividualCancelButton} />
            )}
            <SubmitButton>{saveButtonLabel}</SubmitButton>
          </Flex>
        </Column>
      </Form>
    </FullscreenTakeover.Body>
  );
};

export default EditIndividualForm;
